import React, { useMemo, useState } from "react";
import { Plus } from "lucide-react";
import { useApp } from "../context/AppContext";
import { Autocomplete } from "./Autocomplete";
import { Button } from "./ui/button";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter } from "./ui/dialog";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "./ui/select";
import { useToast } from "../hooks/use-toast";

const inputCls = "w-full px-3.5 py-2.5 rounded-lg border border-slate-200 outline-none focus:border-blue-500 focus:ring-2 focus:ring-blue-100 text-sm";
const empty = { nome: "", categoria: "Medicamento", unidade: "un", precoCompra: 0, precoVenda: 0, estoqueMinimo: 0, estoqueInicial: 0 };

// Botão + modal reutilizável para cadastrar um novo produto no catálogo.
export const NovoProdutoButton = ({ onCreated, className = "", label = "Novo produto", testid = "novo-produto-btn" }) => {
  const { categorias, salvarProduto } = useApp();
  const { toast } = useToast();
  const [open, setOpen] = useState(false);
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  const cats = (categorias || []).map((c) => c.nome);
  const catOptions = cats.length ? cats : ["Medicamento", "Higiene", "Alimentação", "Vestuário", "Outros"];

  const criar = async () => {
    if (!form.nome.trim()) { toast({ title: "Informe o nome do produto" }); return; }
    setSaving(true);
    try {
      const p = await salvarProduto({
        ...form,
        precoCompra: Number(form.precoCompra) || 0, precoVenda: Number(form.precoVenda) || 0,
        estoqueMinimo: Number(form.estoqueMinimo) || 0, estoqueInicial: Number(form.estoqueInicial) || 0,
      });
      if (p?.id) onCreated?.(p);
      setForm(empty); setOpen(false);
      toast({ title: "Produto cadastrado" });
    } catch (e) {
      toast({ title: "Erro ao cadastrar", description: e?.response?.data?.detail || "Sem permissão ou dados inválidos." });
    } finally { setSaving(false); }
  };

  return (
    <>
      <Button type="button" className={`gap-1 bg-emerald-600 hover:bg-emerald-700 text-white ${className}`} onClick={() => { setForm(empty); setOpen(true); }} data-testid={testid}>
        <Plus className="w-4 h-4" /> {label}
      </Button>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md max-h-[90vh] overflow-y-auto" data-testid="produto-inline-modal">
          <DialogHeader><DialogTitle>Novo produto</DialogTitle></DialogHeader>
          <div className="space-y-3">
            <div><label className="text-sm font-medium text-slate-600 mb-1 block">Nome</label>
              <input className={inputCls} value={form.nome} onChange={(e) => setForm({ ...form, nome: e.target.value })} data-testid="produto-inline-nome" autoFocus /></div>
            <div className="grid grid-cols-2 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Categoria</label>
                <Select value={form.categoria} onValueChange={(v) => setForm({ ...form, categoria: v })}>
                  <SelectTrigger data-testid="produto-inline-categoria"><SelectValue /></SelectTrigger>
                  <SelectContent>{catOptions.map((c) => <SelectItem key={c} value={c}>{c}</SelectItem>)}</SelectContent>
                </Select></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Unidade</label>
                <input className={inputCls} value={form.unidade} onChange={(e) => setForm({ ...form, unidade: e.target.value })} placeholder="un, cx, ml..." /></div>
            </div>
            <div className="grid grid-cols-3 gap-3">
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Preço venda</label>
                <input type="number" step="0.01" className={inputCls} value={form.precoVenda} onChange={(e) => setForm({ ...form, precoVenda: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Estoque inicial</label>
                <input type="number" className={inputCls} value={form.estoqueInicial} onChange={(e) => setForm({ ...form, estoqueInicial: e.target.value })} /></div>
              <div><label className="text-sm font-medium text-slate-600 mb-1 block">Estoque mín.</label>
                <input type="number" className={inputCls} value={form.estoqueMinimo} onChange={(e) => setForm({ ...form, estoqueMinimo: e.target.value })} /></div>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setOpen(false)}>Cancelar</Button>
            <Button className="bg-emerald-600 hover:bg-emerald-700" onClick={criar} disabled={saving} data-testid="salvar-produto-inline">{saving ? "Salvando..." : "Salvar produto"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </>
  );
};

// Campo de busca de produto. Com showNovo, mostra o botão "Novo" ao lado (uso legado).
// extraItems permite exibir imediatamente produtos recém-criados fora do contexto.
export const ProdutoField = ({ value, onChange, testid = "produto-busca", placeholder = "Digite o nome do produto...", showNovo = true, extraItems = [] }) => {
  const { produtos } = useApp();
  const [extra, setExtra] = useState([]);

  const items = useMemo(() => {
    const base = (produtos || []).map((p) => ({ id: p.id, label: p.nome, sub: p.categoria || "" }));
    const ids = new Set(base.map((x) => x.id));
    const ex = [...extra, ...extraItems].filter((p) => p && !ids.has(p.id)).map((p) => ({ id: p.id, label: p.nome, sub: p.categoria || "" }));
    return [...base, ...ex];
  }, [produtos, extra, extraItems]);

  return (
    <div className="flex gap-2">
      <div className="flex-1 min-w-0">
        <Autocomplete testid={testid} items={items} value={value} onChange={onChange} placeholder={placeholder} />
      </div>
      {showNovo && (
        <NovoProdutoButton className="shrink-0" onCreated={(p) => { setExtra((x) => [...x, p]); onChange(p.id); }} />
      )}
    </div>
  );
};

export default ProdutoField;
